import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { LinkBtn } from '../components/LinkBtn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { useBranding } from '../lib/branding.tsx';
import { useConfirm } from '../lib/confirm.tsx';
import { questionsDeletedByRemoving } from './categoryRemoval.ts';
import type { Question, QuestionCategory, Questionnaire, QuestionnaireAudience } from '../lib/types.ts';

const AUDIENCE_BADGE: Record<QuestionnaireAudience, string> = {
  company: 'bg-blue-50 text-blue-700',
  driver: 'bg-indigo-50 text-indigo-700',
  vehicle: 'bg-amber-50 text-amber-700',
  contractor: 'bg-teal-50 text-teal-700',
  visitor: 'bg-pink-50 text-pink-700',
  admin_driver: 'bg-purple-50 text-purple-700',
  admin_asset: 'bg-orange-50 text-orange-700',
  audit: 'bg-rose-50 text-rose-700',
};

// Exported for the builder's training-audience mismatch warning, which shows
// the same friendly label the Questionnaires page badges use.
export const AUDIENCE_LABEL: Record<QuestionnaireAudience, string> = {
  company: 'company',
  driver: 'driver',
  vehicle: 'asset',
  contractor: 'contractor',
  visitor: 'visitor',
  admin_driver: 'admin driver',
  admin_asset: 'admin asset',
  audit: 'audit',
};

type Draft = {
  name: string;
  description: string;
  audience: QuestionnaireAudience;
  contractor_types: string[];
  category_ids: string[];
  is_active: boolean;
};

function emptyDraft(): Draft {
  return {
    name: '',
    description: '',
    audience: 'company',
    contractor_types: [],
    category_ids: [],
    is_active: true,
  };
}

export function AdminQuestionnaires() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { branding } = useBranding();
  const contractorTypeOptions = branding.contractorTypeOptions ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ['questionnaires'],
    queryFn: () => adminApi.get<{ questionnaires: Questionnaire[] }>('/api/questionnaires'),
  });

  const [editing, setEditing] = useState<Questionnaire | null>(null);
  const [creating, setCreating] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/questionnaires/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['questionnaires'] }),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => adminApi.post(`/api/questionnaires/${id}/duplicate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questionnaires'] });
      // Duplicate clones categories into new global ones — refresh the category
      // cache so they appear without a hard refresh.
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
  });

  if (isLoading) return <CenteredSpinner />;

  const list = data?.questionnaires ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Questionnaires</h1>
          <p className="mt-1 text-sm text-slate-500">
            Create separate questionnaires for different audiences and contractor types. Each
            questionnaire has its own categories and questions.
          </p>
        </div>
        <Btn onClick={() => setCreating(true)}>+ New questionnaire</Btn>
      </div>

      {list.length === 0 ? (
        <EmptyState
          title="No questionnaires yet"
          description="Create your first questionnaire to start collecting structured responses."
        />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          <ul className="divide-y divide-slate-100">
            {list.map((q) => (
              <li key={q.id} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{q.name}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${AUDIENCE_BADGE[q.audience] ?? 'bg-blue-50 text-blue-700'}`}
                    >
                      {AUDIENCE_LABEL[q.audience] ?? q.audience}
                    </span>
                    {!q.is_active && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        inactive
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {q.contractor_types && q.contractor_types.length > 0
                      ? `Contractor types: ${q.contractor_types.join(', ')}`
                      : 'Applies to all contractor types'}
                  </div>
                  {q.description && (
                    <div className="mt-1 text-sm text-slate-600">{q.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <LinkBtn to={`/admin/questions?questionnaireId=${q.id}`} size="sm">
                    Edit questions
                  </LinkBtn>
                  <Btn size="sm" variant="secondary" onClick={() => setEditing(q)}>
                    Settings
                  </Btn>
                  <Btn
                    size="sm"
                    variant="secondary"
                    loading={duplicateMutation.isPending && duplicateMutation.variables === q.id}
                    onClick={() => {
                      void (async () => {
                        if (
                          await confirm({
                            title: 'Duplicate questionnaire',
                            body: (
                              <>
                                This copies <span className="font-semibold">{q.name}</span> and all
                                its questions.
                              </>
                            ),
                            confirmLabel: 'Duplicate',
                          })
                        )
                          duplicateMutation.mutate(q.id);
                      })();
                    }}
                  >
                    Duplicate
                  </Btn>
                  <Btn
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      void (async () => {
                        if (
                          await confirm({
                            title: 'Delete questionnaire',
                            body: (
                              <>
                                Are you sure you want to delete{' '}
                                <span className="font-semibold">{q.name}</span>? This can&apos;t be
                                undone.
                              </>
                            ),
                            confirmLabel: 'Delete',
                            danger: true,
                          })
                        )
                          deleteMutation.mutate(q.id);
                      })();
                    }}
                  >
                    Delete
                  </Btn>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {creating && (
        <QuestionnaireEditor
          mode="create"
          contractorTypeOptions={contractorTypeOptions}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <QuestionnaireEditor
          mode="edit"
          existing={editing}
          contractorTypeOptions={contractorTypeOptions}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function QuestionnaireEditor({
  mode,
  existing,
  contractorTypeOptions,
  onClose,
}: {
  mode: 'create' | 'edit';
  existing?: Questionnaire;
  contractorTypeOptions: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: categoriesData } = useQuery({
    queryKey: ['question-categories', 'all'],
    queryFn: () =>
      adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories'),
  });
  const categories = categoriesData?.categories ?? [];

  // Edit mode only: load this questionnaire's questions so an untick-to-remove
  // save can warn about (and is what server-side then deletes) the questions in
  // any category being dropped. Shares the builder's ['questions', id] cache.
  const { data: questionsData } = useQuery({
    queryKey: ['questions', existing?.id ?? 'new'],
    queryFn: () =>
      adminApi.get<{ questions: Question[] }>(
        `/api/questions?questionnaire_id=${existing!.id}`,
      ),
    enabled: mode === 'edit' && !!existing,
  });
  const questionnaireQuestions = questionsData?.questions ?? [];

  const [draft, setDraft] = useState<Draft>(() =>
    existing
      ? {
          name: existing.name,
          description: existing.description ?? '',
          audience: existing.audience,
          contractor_types: existing.contractor_types ?? [],
          category_ids: [],
          is_active: existing.is_active,
        }
      : emptyDraft(),
  );
  const [error, setError] = useState<unknown>(null);

  // When editing, the checked categories are those whose questionnaire_ids
  // include this questionnaire. Categories load asynchronously, so derive the
  // checked set once both the questionnaire and its categories are available.
  useEffect(() => {
    if (existing) {
      setDraft({
        name: existing.name,
        description: existing.description ?? '',
        audience: existing.audience,
        contractor_types: existing.contractor_types ?? [],
        category_ids: categories
          .filter((c) => c.questionnaire_ids.includes(existing.id))
          .map((c) => c.id),
        is_active: existing.is_active,
      });
    }
  }, [existing, categoriesData]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        audience: draft.audience,
        contractor_types: draft.contractor_types.length === 0 ? null : draft.contractor_types,
        category_ids: draft.category_ids,
        is_active: draft.is_active,
      };
      if (mode === 'create') {
        return adminApi.post('/api/questionnaires', payload);
      }
      return adminApi.patch(`/api/questionnaires/${existing!.id}`, payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questionnaires'] });
      // Saving rewrites the questionnaire_categories join, which feeds each
      // category's questionnaire_ids — the source the editor derives its ticked
      // set from. Without this the cached categories stay stale (30s staleTime),
      // so reopening showed the pre-save ticks until a hard refresh. Prefix-match
      // refreshes every category query (Settings, builder, this editor).
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
      // Removing a category deletes its questions server-side; refresh the
      // builder's question cache so they disappear without a hard refresh.
      void qc.invalidateQueries({ queryKey: ['questions'] });
      onClose();
    },
    onError: setError,
  });

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function toggleContractorType(value: string) {
    set(
      'contractor_types',
      draft.contractor_types.includes(value)
        ? draft.contractor_types.filter((v) => v !== value)
        : [...draft.contractor_types, value],
    );
  }

  function toggleCategory(id: string) {
    set(
      'category_ids',
      draft.category_ids.includes(id)
        ? draft.category_ids.filter((v) => v !== id)
        : [...draft.category_ids, id],
    );
  }

  // Linked categories the user has unticked that still contain questions —
  // saving will permanently delete those questions and their company data.
  async function handleSave() {
    if (existing) {
      const removedWithQuestions = categories
        .filter(
          (c) =>
            c.questionnaire_ids.includes(existing.id) &&
            !draft.category_ids.includes(c.id),
        )
        .map((c) => ({
          name: c.name,
          count: questionsDeletedByRemoving(questionnaireQuestions, c.id).filter(
            (q) => !q.instance_of,
          ).length,
        }))
        .filter((r) => r.count > 0);

      if (removedWithQuestions.length > 0) {
        const ok = await confirm({
          title: removedWithQuestions.length === 1 ? 'Remove category' : 'Remove categories',
          body: (
            <div className="space-y-2">
              <p>
                Saving permanently deletes these questions and any answers/files companies
                submitted for them:
              </p>
              <ul className="list-disc pl-5">
                {removedWithQuestions.map((r) => (
                  <li key={r.name}>
                    {r.name} ({r.count} question{r.count === 1 ? '' : 's'})
                  </li>
                ))}
              </ul>
            </div>
          ),
          confirmLabel: 'Save',
          danger: true,
        });
        if (!ok) return;
      }
    }
    saveMutation.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'create' ? 'New questionnaire' : `Edit ${existing!.name}`}
      size="md"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={() => void handleSave()}
            loading={saveMutation.isPending}
            disabled={!draft.name.trim()}
          >
            {mode === 'create' ? 'Create' : 'Save'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required>
          <TextInput value={draft.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Description">
          <TextArea
            value={draft.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Audience" required>
            <Select
              value={draft.audience}
              onChange={(e) => set('audience', e.target.value as QuestionnaireAudience)}
            >
              <option value="company">Company</option>
              <option value="driver">Driver</option>
              <option value="audit">Audit (assigned to an auditor)</option>
              <option value="vehicle">Asset (admin-run audit)</option>
              <option value="contractor">General contractor</option>
              <option value="visitor">Visitor</option>
              <option value="admin_driver">Admin driver (operating company)</option>
              <option value="admin_asset">Admin asset (operating company)</option>
            </Select>
          </Field>
          <Field label="Status">
            <label className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
              />
              Active
            </label>
          </Field>
        </div>

        <Field
          label="Contractor types"
          hint="Leave all unticked to make this questionnaire apply to every contractor type."
        >
          {contractorTypeOptions.length === 0 ? (
            <div className="text-xs italic text-slate-500">
              No contractor types configured under Settings yet. Without any, this questionnaire
              applies to every contractor type.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1">
              {contractorTypeOptions.map((t) => (
                <label key={t} className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={draft.contractor_types.includes(t)}
                    onChange={() => toggleContractorType(t)}
                  />
                  {t}
                </label>
              ))}
            </div>
          )}
        </Field>

        <Field
          label="Categories"
          hint="Tick the question categories this questionnaire includes. Categories are global and can be shared across questionnaires."
        >
          {categories.length === 0 ? (
            <div className="text-xs italic text-slate-500">
              No categories configured under Settings yet. Create a category first, then come
              back to include it in this questionnaire.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <label key={c.id} className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={draft.category_ids.includes(c.id)}
                    onChange={() => toggleCategory(c.id)}
                  />
                  {c.name}
                </label>
              ))}
            </div>
          )}
        </Field>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
